import React, { useEffect, useState } from "react";

import { practiceReservationsService } from "api/services/practice-reservations.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import { extractList, getApiErrorMessage } from "utils/api-error";

import type { PracticeReservation } from "api/types/practice-reservations.types";
import "pages/AdminPage.scss";
import "./PracticeReservationList.scss";

const STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "draft" | "info"> = {
  RESERVED: "info",
  COMPLETED: "active",
  CANCELLED: "rejected",
};

const PAYMENT_BADGE: Record<string, "active" | "pending" | "rejected" | "draft" | "info"> = {
  NOT_REQUIRED: "draft",
  PENDING: "pending",
  PAID: "active",
  REJECTED: "rejected",
  EXPIRED: "draft",
};

const PracticeReservationList: React.FC = () => {
  const [items, setItems] = useState<PracticeReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");

  // 결제 승인/거절 모달
  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    type: "complete" | "reject" | null;
    item: PracticeReservation | null;
  }>({ open: false, type: null, item: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, unknown> = {};
      if (statusFilter) params.status = statusFilter;
      if (paymentFilter) params.payment_status = paymentFilter;
      const res = await practiceReservationsService.list(params);
      setItems(extractList<PracticeReservation>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, paymentFilter]);

  const handlePaymentAction = async () => {
    if (!paymentModal.item || !paymentModal.type) return;
    setActionLoading(true);
    try {
      if (paymentModal.type === "complete") {
        await practiceReservationsService.paymentComplete(paymentModal.item.id);
      } else {
        await practiceReservationsService.paymentReject(paymentModal.item.id);
      }
      setPaymentModal({ open: false, type: null, item: null });
      fetchList();
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const columns: Column<PracticeReservation>[] = [
    { key: "user_id", title: "사용자", width: "120px" },
    { key: "schedule_title", title: "스케줄", width: "180px" },
    { key: "date", title: "날짜", width: "110px" },
    {
      key: "start_time",
      title: "시간",
      width: "120px",
      render: (v, row) => `${String(v).slice(0, 5)} ~ ${String(row.end_time).slice(0, 5)}`,
    },
    { key: "location_name", title: "장소", width: "100px" },
    {
      key: "status",
      title: "예약상태",
      width: "90px",
      render: (v) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{String(v)}</Badge>,
    },
    {
      key: "payment_status",
      title: "결제상태",
      width: "90px",
      render: (v, row) => <Badge variant={PAYMENT_BADGE[String(v)] ?? "draft"}>{row.payment_status_display}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      width: "150px",
      render: (_, row) => {
        if (row.payment_status !== "PENDING") return null;
        return (
          <div className="reservation-actions">
            <Button size="sm" variant="primary" onClick={() => setPaymentModal({ open: true, type: "complete", item: row })}>
              결제 승인
            </Button>
            <Button size="sm" variant="danger" onClick={() => setPaymentModal({ open: true, type: "reject", item: row })}>
              거절
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="연습 예약 관리" description="연습 예약 목록을 관리하고 결제를 승인/거절합니다." />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "", label: "예약상태 전체" },
              { value: "RESERVED", label: "예약됨" },
              { value: "COMPLETED", label: "완료" },
              { value: "CANCELLED", label: "취소됨" },
            ]}
          />
          <Select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            options={[
              { value: "", label: "결제상태 전체" },
              { value: "PENDING", label: "결제 대기" },
              { value: "PAID", label: "결제 완료" },
              { value: "REJECTED", label: "거절됨" },
              { value: "EXPIRED", label: "만료" },
            ]}
          />
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={items} rowKey="id" />}
      </div>

      {/* 결제 승인/거절 모달 */}
      <Modal
        open={paymentModal.open}
        title={paymentModal.type === "complete" ? "결제 승인" : "결제 거절"}
        width="440px"
        onClose={() => setPaymentModal({ open: false, type: null, item: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setPaymentModal({ open: false, type: null, item: null })}>
              취소
            </Button>
            <Button variant={paymentModal.type === "reject" ? "danger" : "primary"} onClick={handlePaymentAction} disabled={actionLoading}>
              {actionLoading ? "처리 중..." : paymentModal.type === "complete" ? "승인" : "거절"}
            </Button>
          </div>
        }
      >
        <p className="confirm-text">
          <strong>{paymentModal.item?.user_id}</strong>님의 예약 결제를 {paymentModal.type === "complete" ? "승인" : "거절"}하시겠습니까?
        </p>
      </Modal>
    </div>
  );
};

export default PracticeReservationList;
